import { enumify, isDate, isDateString, isNumber, isObjectLike, isOptional, isString, TypeGuard } from '../lib/typeInference.js';
import { ICreateNotificationFromScheduledTask, isCreateNotificationFromScheduledTask } from './pushNotification.types.js';
import { isRelativeTimeString, RelativeTimeString } from './metric/period.types.js';

export const scheduledTaskStatusValues = [
    'DRAFT', // Just saved, not scheduled
    'SCHEDULED', // waiting to be queued
    'QUEUED', // waiting to be processed
    'PROCESSING', // Pulled from queue, actively being processed
    'PROCESSED',
    'ERROR' // Failed to process, may be up for retry
] as const;
export type ScheduledTaskStatus = (typeof scheduledTaskStatusValues)[number];
export const ScheduledTaskStatusEnum = enumify(scheduledTaskStatusValues);
function isScheduledTaskStatus(status: unknown): status is ScheduledTaskStatus {
    return scheduledTaskStatusValues.includes(status as any);
}

export const scheduledTaskTypeValues = [
    // Debug types
    'NOOP',
    'FORCE_ERROR',
    // Real types
    'CREATE_PUSH_NOTIFICATION',
    'RESET_AWARD_POINTS'
] as const;
export type ScheduledTaskType = (typeof scheduledTaskTypeValues)[number];
export const ScheduledTaskTypeEnum = enumify(scheduledTaskTypeValues);
function isScheduledTaskType(type: unknown): type is ScheduledTaskType {
    return scheduledTaskTypeValues.includes(type as any);
}
export interface IQueuedScheduledTask {
    _id: string;
    tenantId: string;
}

export function isQueuedScheduledTask(task: any): task is IQueuedScheduledTask {
    return task && isString(task._id) && isString(task.tenantId);
}

export type IScheduledTaskSource =
    | {
          source: 'RULE';
          ruleId: string;
      }
    | {
          source: 'API';
      };

export function isScheduledTaskSource(source: any): source is IScheduledTaskSource {
    return source && isString(source.source) && ((source.source === 'RULE' && isString(source.ruleId)) || source.source === 'API');
}

export interface IScheduledTaskParameterTypeMap<IDTYPE> {
    [ScheduledTaskTypeEnum.NOOP]: NonNullable<any>;
    [ScheduledTaskTypeEnum.FORCE_ERROR]: NonNullable<any>;
    [ScheduledTaskTypeEnum.CREATE_PUSH_NOTIFICATION]: ICreateNotificationFromScheduledTask<IDTYPE>;
    [ScheduledTaskTypeEnum.RESET_AWARD_POINTS]: { userId: string; awardId: string };
}

export function isScheduledTaskParameters<IDTYPE, Type extends ScheduledTaskType>(
    type: Type,
    parameters: unknown,
    isIDTYPE: TypeGuard<IDTYPE>
): parameters is IScheduledTaskParameterTypeMap<IDTYPE>[Type] {
    switch (type) {
        case 'NOOP':
        case 'FORCE_ERROR':
            return true;
        case 'CREATE_PUSH_NOTIFICATION':
            return isCreateNotificationFromScheduledTask(parameters, isIDTYPE);
        case 'RESET_AWARD_POINTS':
            return isObjectLike(parameters) && isString(parameters?.userId) && isString(parameters?.awardId);
        default:
            return false;
    }
}

// FIXME use of generic leads to ''Type' could be instantiated with a different subtype of constraint ' error.  Avoid generic functions, or remodel as a union of 4 types
export interface IScheduledTask<IDTYPE, Type extends ScheduledTaskType> {
    _id: IDTYPE;
    tenantId: IDTYPE;
    locationId?: IDTYPE;
    status: ScheduledTaskStatus;
    type: Type;
    parameters: IScheduledTaskParameterTypeMap<IDTYPE>[Type];
    triggerAt: Date;
    queuedAt?: Date;
    attempts: number;
    error?: string;
    source: IScheduledTaskSource;
    idempotencyKey?: string;
}

export function isScheduledTask<IDTYPE, Type extends ScheduledTaskType>(task: unknown, isIDTYPE: TypeGuard<IDTYPE>): task is IScheduledTask<IDTYPE, Type> {
    return (
        isObjectLike(task) &&
        isIDTYPE(task._id) &&
        isIDTYPE(task.tenantId) &&
        isOptional(isIDTYPE, task.locationId) &&
        scheduledTaskStatusValues.includes(task.status as any) &&
        isScheduledTaskType(task.type) &&
        isScheduledTaskParameters(task.type, task.parameters, isIDTYPE) &&
        isDate(task.triggerAt) &&
        isOptional(isDate, task.queuedAt) &&
        isNumber(task.attempts) &&
        isOptional(isString, task.error) &&
        isScheduledTaskSource(task.source) &&
        isOptional(isString, task.idempotencyKey)
    );
}

export type ICreateScheduledTask<IDTYPE, Type extends ScheduledTaskType> = Pick<
    IScheduledTask<IDTYPE, Type>,
    'tenantId' | 'locationId' | 'status' | 'type' | 'parameters' | 'triggerAt' | 'source' | 'idempotencyKey'
>;

export function isCreateScheduledTask<IDTYPE, Type extends ScheduledTaskType>(task: unknown, isIDTYPE: TypeGuard<IDTYPE>): task is ICreateScheduledTask<IDTYPE, Type> {
    return (
        isObjectLike(task) &&
        isIDTYPE(task.tenantId) &&
        isOptional(isIDTYPE, task.locationId) &&
        scheduledTaskStatusValues.includes(task.status as any) &&
        isScheduledTaskType(task.type) &&
        isScheduledTaskParameters(task.type, task.parameters, isIDTYPE) &&
        isDate(task.triggerAt) &&
        isScheduledTaskSource(task.source) &&
        isOptional(isString, task.idempotencyKey)
    );
}

export type ICreateScheduledTaskBody<IDTYPE, Type extends ScheduledTaskType> = Pick<ICreateScheduledTask<IDTYPE, Type>, 'type' | 'status' | 'parameters'> & {
    locationId?: string;
    triggerAt: string;
};

// api types go in sdk-shared-api or api-server
// export const CreateScheduledTaskBodySchema = z
//     .object({
//         locationId: z.string().optional().refine(isOptional_Curried(isObjectIdLike)),
//         status: z.nativeEnum(ScheduledTaskStatus).refine(status => [ScheduledTaskStatus.DRAFT, ScheduledTaskStatus.SCHEDULED].includes(status)),
//         type: z.nativeEnum(ScheduledTaskType),
//         parameters: z.any(),
//         triggerAt: z.string().refine(isDateString)
//     })
//     .refine(value => isScheduledTaskParameters(value.type, value.parameters));

// export function isCreateScheduledTaskBody<Type extends ScheduledTaskType>(task: any): task is ICreateScheduledTaskBody<Type> {
//     return CreateScheduledTaskBodySchema.safeParse(task).success;
// }

export interface ICreateScheduledTaskFromImpactParameterTypeMap {
    [ScheduledTaskTypeEnum.NOOP]: IScheduledTaskParameterTypeMap<string>['NOOP'];
    [ScheduledTaskTypeEnum.FORCE_ERROR]: IScheduledTaskParameterTypeMap<string>['FORCE_ERROR'];
    [ScheduledTaskTypeEnum.CREATE_PUSH_NOTIFICATION]: IScheduledTaskParameterTypeMap<string>['CREATE_PUSH_NOTIFICATION'];
    [ScheduledTaskTypeEnum.RESET_AWARD_POINTS]: { perkId: string };
}

export function isCreateScheduledTaskFromImpactParameters<Type extends ScheduledTaskType>(
    type: Type,
    parameters: unknown
): parameters is IScheduledTaskParameterTypeMap<string>[Type] {
    switch (type) {
        case 'NOOP':
        case 'FORCE_ERROR':
            return true;
        case 'CREATE_PUSH_NOTIFICATION':
            return isScheduledTaskParameters(type, parameters, isString);
        case 'RESET_AWARD_POINTS':
            return isObjectLike(parameters) && isString(parameters?.perkId);
        default:
            return false;
    }
}

export type ICreateScheduledTaskFromImpact<Type extends ScheduledTaskType> = {
    type: Type;
    // FIXME compiler is interpreting this as any
    parameters: ICreateScheduledTaskFromImpactParameterTypeMap[Type];
    triggerAt: string | RelativeTimeString;
};

export function isCreateScheduledTaskFromImpact<Type extends ScheduledTaskType>(task: unknown): task is ICreateScheduledTaskFromImpact<Type> {
    return (
        isObjectLike(task) &&
        isScheduledTaskType(task.type) &&
        isCreateScheduledTaskFromImpactParameters(task.type, task.parameters) &&
        (isDateString(task.triggerAt) || isRelativeTimeString(task.triggerAt))
    );
}

export type IUpdateScheduledTask<IDTYPE, Type extends ScheduledTaskType> = Pick<IScheduledTask<IDTYPE, Type>, 'status' | 'type' | 'parameters' | 'triggerAt'>;

export function isUpdateScheduledTask<IDTYPE, Type extends ScheduledTaskType>(task: unknown, isIDTYPE: TypeGuard<IDTYPE>): task is IUpdateScheduledTask<IDTYPE, Type> {
    return (
        isObjectLike(task) &&
        isOptional(isScheduledTaskStatus, task.status) &&
        isScheduledTaskType(task.type) &&
        isScheduledTaskParameters(task.type, task.parameters, isIDTYPE) &&
        isOptional(isDateString, task.triggerAt)
    );
}

export type IUpdateScheduledTaskBody<IDTYPE, Type extends ScheduledTaskType> = Pick<IUpdateScheduledTask<IDTYPE, Type>, 'type' | 'status' | 'parameters'> & {
    locationId?: string;
    triggerAt?: string;
};

// export const UpdateScheduledTaskBodySchema = z
//     .object({
//         locationId: z.string().optional().refine(isOptional_Curried(isObjectIdLike)),
//         status: z
//             .nativeEnum(ScheduledTaskStatus)
//             .optional()
//             .refine(status => !status || [ScheduledTaskStatus.DRAFT, ScheduledTaskStatus.SCHEDULED].includes(status)),
//         type: z.nativeEnum(ScheduledTaskType),
//         parameters: z.any(),
//         triggerAt: z.string().optional().refine(isDateString)
//     })
//     .refine(value => isScheduledTaskParameters(value.type, value.parameters));

// export function isUpdateScheduledTaskBody<Type extends ScheduledTaskType>(task: any): task is IUpdateScheduledTaskBody<Type> {
//     return UpdateScheduledTaskBodySchema.safeParse(task).success;
// }

// Shortcut for specific types

export type IScheduledTaskNoop<IDTYPE> = IScheduledTask<IDTYPE, 'NOOP'>;
export type ICreateScheduledTaskNoop<IDTYPE> = ICreateScheduledTask<IDTYPE, 'NOOP'>;
export type IUdpateScheduledTaskNoop<IDTYPE> = IUpdateScheduledTask<IDTYPE, 'NOOP'>;
export type IScheduledTaskForceError<IDTYPE> = IScheduledTask<IDTYPE, 'FORCE_ERROR'>;
export type ICreateScheduledTaskForceError<IDTYPE> = ICreateScheduledTask<IDTYPE, 'FORCE_ERROR'>;
export type IUpdateScheduledTaskForceError<IDTYPE> = IUpdateScheduledTask<IDTYPE, 'FORCE_ERROR'>;
export type IScheduledTaskCreatePushNotification<IDTYPE> = IScheduledTask<IDTYPE, 'CREATE_PUSH_NOTIFICATION'>;
export type ICreateScheduledTaskCreatePushNotification<IDTYPE> = ICreateScheduledTask<IDTYPE, 'CREATE_PUSH_NOTIFICATION'>;
export type IUpdateScheduledTaskCreatePushNotification<IDTYPE> = IUpdateScheduledTask<IDTYPE, 'CREATE_PUSH_NOTIFICATION'>;
export type IScheduledTaskResetAwardPoints<IDTYPE> = IScheduledTask<IDTYPE, 'RESET_AWARD_POINTS'>;
export type ICreateScheduledTaskResetAwardPoints<IDTYPE> = ICreateScheduledTask<IDTYPE, 'RESET_AWARD_POINTS'>;
export type IUpdateScheduledTaskResetAwardPoints<IDTYPE> = IUpdateScheduledTask<IDTYPE, 'RESET_AWARD_POINTS'>;

